// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// COB-462 — where the COB-178 clear-sign anchor gets sealed.
//
// The anchor is the triple `{user_payout, platform_refund, deposit}` (plus the
// locally-derived `U`) that `signPsbt` cross-checks a withdrawal PSBT against
// before it will sign. COB-178 sealed it at vault *creation*; COB-245 then
// stopped returning `deposit_address` in the create response — it is withheld
// until the owner has confirmed the registered contract on the independent
// verifier — so from 2026-07-04 the create-time seal had nothing to seal and
// every vault fell through to the sign-time heal path.
//
// The replacement moment is **contract acknowledgement**: the first instant the
// browser can hold all three destinations, and an instant the user actively
// participated in. Two call sites drive it, both off `VerifierConfirmModal`'s
// `onConfirmed`:
//   * `pages/accept.tsx`   — creation success step. Seals the payout address
//     the browser CHOSE locally (`pickPayoutAddress`), never the server's echo
//     of it, plus the deposit address the post-ack details fetch reveals.
//   * `pages/vault-detail.tsx` — the same ack from the vault page, and (by
//     design) a repair pass for every vault created between 2026-07-04 and
//     this change, on its next visit.
//
// Every helper here is best-effort by contract: a seal failure must never
// block the acknowledgement, the navigation, or the page. The agent's own
// heal-or-fail-closed path still covers a missing anchor at sign time — this
// is a strictly earlier, strictly better-sourced capture, not a new gate.

import type { AnchorHint } from '@cobault/key-agent';
// Zero-runtime-import module (HW.3): the typed error classes are usable from
// UI code without pulling ledger-bitcoin's module graph into the entry chunk.
import { UnsupportedOnHardwareError } from '@cobault/key-agent/ledger-errors';

/** The single method of the session's key agent this module needs. Narrow on
 *  purpose — every backend satisfies it (the hardware one by throwing). */
export interface AnchorSealingAgent {
  sealVaultAnchor(vaultId: string, hint: AnchorHint): Promise<void>;
}

/** What a seal attempt did. Returned (never thrown) so call sites can stay
 *  fire-and-forget while tests can still assert on the branch taken. */
export type AnchorSealOutcome =
  /** The agent accepted the anchor (or already held one — the seal is
   *  write-once and idempotent on both software backends). */
  | 'sealed'
  /** Nothing to seal: no agent session, or the deposit address is still
   *  withheld (the acknowledgement has not taken effect server-side yet), or
   *  a contract address was absent. Not an error. */
  | 'skipped'
  /** Hardware backend: the device path is anchorless by design (HW.3), so
   *  `sealVaultAnchor` throws `UnsupportedOnHardwareError`. Expected, silent. */
  | 'anchorless-device'
  /** Something went wrong and was warned about. Non-fatal by contract. */
  | 'failed';

/** The Ledger backend's "this method does not exist on a device" throw. Typed
 *  check first; the name fallback keeps the classification correct even if the
 *  error crossed a boundary that lost its prototype (a Worker round-trip
 *  reconstructs errors by name — see `key-agent-worker-client.ts`). */
export function isAnchorlessDeviceError(err: unknown): boolean {
  return (
    err instanceof UnsupportedOnHardwareError ||
    (err as { name?: string } | null)?.name === 'UnsupportedOnHardwareError'
  );
}

/** Seal `vaultId`'s anchor if — and only if — all three destinations are in
 *  hand. Never throws. The write-once semantics live in the agent, so callers
 *  may call this unconditionally: an anchor sealed earlier always wins. */
export async function sealVaultAnchorBestEffort(
  agent: AnchorSealingAgent | null | undefined,
  vaultId: string,
  addresses: {
    userPayoutAddress: string | null | undefined;
    platformRefundAddress: string | null | undefined;
    depositAddress: string | null | undefined;
  },
): Promise<AnchorSealOutcome> {
  const { userPayoutAddress, platformRefundAddress, depositAddress } = addresses;
  if (!agent) return 'skipped';
  if (!userPayoutAddress || !platformRefundAddress || !depositAddress) {
    return 'skipped';
  }
  try {
    await agent.sealVaultAnchor(vaultId, {
      userPayoutAddress,
      platformRefundAddress,
      depositAddress,
    });
    return 'sealed';
  } catch (e) {
    if (isAnchorlessDeviceError(e)) return 'anchorless-device';
    // Non-fatal by contract. The common benign case is a session that did not
    // create this vault on this device and so has no local BIP44 index to
    // derive `U` from; the sign-time heal path seals it later from the same
    // authenticated details response.
    console.warn(`COB-462: could not seal the vault anchor for ${vaultId}`, e);
    return 'failed';
  }
}

/** Post-acknowledgement seal for the creation success step.
 *
 *  `userPayoutAddress` MUST be the address this browser chose for the vault
 *  (`pickPayoutAddress`), not one read back off a response: the whole point of
 *  the anchor is that the payout destination was pinned by the client. The
 *  deposit address can only come from the server — it is derived from the
 *  three-party descriptor — and is fetched here because the acknowledgement is
 *  exactly what unlocks it (COB-245).
 *
 *  Dependencies are injected rather than imported so this is directly
 *  unit-testable without a network or a Worker. */
export async function sealVaultAnchorAfterAcknowledgement(deps: {
  agent: AnchorSealingAgent | null | undefined;
  vaultId: string;
  /** The locally-chosen payout address for this vault. */
  userPayoutAddress: string | null | undefined;
  /** From the create response's contract. */
  platformRefundAddress: string | null | undefined;
  /** Typically `api.getVaultDetails`. */
  getVaultDetails: (vaultId: string) => Promise<{ deposit_address?: string }>;
  /** Called with the now-revealed deposit address before the seal, so the
   *  caller can persist it (the local vault store caches it for the list). */
  onDepositAddressRevealed?: (depositAddress: string) => void;
}): Promise<AnchorSealOutcome> {
  let depositAddress: string | undefined;
  try {
    const details = await deps.getVaultDetails(deps.vaultId);
    depositAddress = details.deposit_address;
  } catch (e) {
    console.warn(
      `COB-462: could not read vault ${deps.vaultId} after acknowledgement; ` +
        'the anchor will be sealed on the vault page instead',
      e,
    );
    return 'failed';
  }
  if (depositAddress) deps.onDepositAddressRevealed?.(depositAddress);
  return sealVaultAnchorBestEffort(deps.agent, deps.vaultId, {
    userPayoutAddress: deps.userPayoutAddress,
    platformRefundAddress: deps.platformRefundAddress,
    depositAddress,
  });
}
